import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Copy, Pencil, Trash2, History, Tags, FileUp, Sparkles, Archive, ArchiveRestore, ShieldCheck, ShieldAlert } from 'lucide-react';
import { chipClass, chipStyle } from './categoryUtils';
import { AttachmentList } from './AttachmentList';

export const BlockDetailDrawer = ({ block: b, onOpenChange, canManage, catColors, onCopy, onEdit, onDelete, onHistory, onMeta }) => (
  <Sheet open={!!b} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="rfp-detail-drawer">
      {b && (
        <>
          <SheetHeader className="text-left space-y-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono font-bold text-[#0F2040] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"
                data-testid="rfp-drawer-capid">{b.capability_id}</span>
              {(b.category_names || []).map(name => (
                <Badge key={name} variant="outline"
                  className={`text-[9px] ${chipStyle(catColors?.[name]) ? '' : chipClass(name)}`}
                  style={chipStyle(catColors?.[name]) || undefined}>{name}</Badge>
              ))}
              {b.archived && <Badge variant="outline" className="text-[9px] bg-slate-100 text-slate-500 border-slate-300" data-testid="rfp-drawer-archived-badge">Archived</Badge>}
              {b.review_status === 'pending' && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid="rfp-drawer-review-badge">Review pending</Badge>}
            </div>
            <SheetTitle className="text-lg text-[#0F2040]" data-testid="rfp-drawer-title">{b.title}</SheetTitle>
          </SheetHeader>

          <div className="flex items-center gap-2 mt-3 mb-4">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onCopy(b)} data-testid="rfp-drawer-copy">
              <Copy className="w-3 h-3 mr-1" />Copy
            </Button>
            {canManage && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onEdit(b)} data-testid="rfp-drawer-edit">
                  <Pencil className="w-3 h-3 mr-1" />Edit
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onHistory(b)} data-testid="rfp-drawer-history">
                  <History className="w-3 h-3 mr-1" />History
                </Button>
                {onMeta && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs" title={b.archived ? 'Restore to library' : 'Archive (hidden by default)'}
                      onClick={() => onMeta(b, { archived: !b.archived })} data-testid="rfp-drawer-archive">
                      {b.archived ? <ArchiveRestore className="w-3 h-3 mr-1" /> : <Archive className="w-3 h-3 mr-1" />}{b.archived ? 'Restore' : 'Archive'}
                    </Button>
                    <Button size="sm" variant="outline" className={`h-7 text-xs ${b.review_status === 'pending' ? 'text-emerald-700 border-emerald-200' : 'text-amber-700 border-amber-200'}`}
                      title={b.review_status === 'pending' ? 'Mark as confirmed' : 'Flag for review'}
                      onClick={() => onMeta(b, { review_status: b.review_status === 'pending' ? 'confirmed' : 'pending' })} data-testid="rfp-drawer-review-toggle">
                      {b.review_status === 'pending' ? <ShieldCheck className="w-3 h-3 mr-1" /> : <ShieldAlert className="w-3 h-3 mr-1" />}
                      {b.review_status === 'pending' ? 'Confirm' : 'Needs review'}
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 ml-auto"
                  onClick={() => onDelete(b)} data-testid="rfp-drawer-delete">
                  <Trash2 className="w-3 h-3 mr-1" />Delete
                </Button>
              </>
            )}
          </div>

          <div className="text-sm text-slate-700 leading-relaxed rfp-md border-t border-slate-100 pt-3" data-testid="rfp-drawer-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{b.body}</ReactMarkdown>
          </div>

          {(b.attachments || []).length > 0 && (
            <div className="mt-3" data-testid="rfp-drawer-attachments">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Attachments</p>
              <AttachmentList attachments={b.attachments} testId="rfp-drawer-attach-list" />
            </div>
          )}

          {(b.keywords || []).length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1 mb-1.5">
                <Tags className="w-3 h-3" />Keywords
                {b.keywords_source === 'ai' && <Sparkles className="w-2.5 h-2.5 text-[#4ABFCF]" />}
              </p>
              <div className="flex flex-wrap gap-1" data-testid="rfp-drawer-keywords">
                {b.keywords.map(k => (
                  <span key={k} className="text-[9px] bg-slate-50 text-slate-600 border border-slate-200 rounded-full px-2 py-0.5">{k}</span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-slate-100 text-[10px] text-slate-400 space-y-0.5" data-testid="rfp-drawer-meta">
            <p>Updated {b.updated_at ? new Date(b.updated_at).toLocaleDateString() : '—'}
              {b.updated_by && b.updated_by !== 'system' ? ` by ${b.updated_by}` : ''}</p>
            {b.scrubbed_at && (
              <button type="button" onClick={() => onHistory(b)} className="flex items-center gap-1 text-emerald-700 hover:underline" data-testid="rfp-drawer-scrubbed"
                title="Sensitive content removed — the previous wording is restorable from the block history">
                <ShieldCheck className="w-2.5 h-2.5" />Scrubbed {new Date(b.scrubbed_at).toLocaleDateString()}{b.scrubbed_by ? ` by ${b.scrubbed_by}` : ''}
              </button>
            )}
            {b.imported_from && (
              <p className="flex items-center gap-1"><FileUp className="w-2.5 h-2.5" />Imported from {b.imported_from}</p>
            )}
            <p>Copied {b.copy_count || 0}× · included in {b.include_count || 0} RFP packs</p>
          </div>
        </>
      )}
    </SheetContent>
  </Sheet>
);
